/**
 * Shared hook: fetches GET /api/brand/demo-profile once per session and caches
 * the result so PortfolioPage / WhatsTrendingPage / InsightsAnalyticsPage
 * can override their hardcoded fixtures with brand-specific data when the
 * caller is a seeded demo brand.
 *
 * Returns:
 *   { profile, loading, isDemo }
 *
 * Non-demo brands receive `isDemo === false` and pages keep their original
 * fallback fixtures (no behavior change for real users).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
let _cache = null;
let _inflight = null;

export function useDemoProfile() {
  const [profile, setProfile] = useState(_cache);
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    let dead = false;
    // Only treat the cache as authoritative if it's a positive (is_demo:true).
    // Negative results often mean "user wasn't logged in yet at the time of
    // the previous fetch" — re-fetch on every mount until we either get a
    // demo-positive or a definitive auth-OK negative.
    if (_cache && _cache.is_demo) {
      setLoading(false);
      return;
    }
    if (!_inflight) {
      _inflight = axios.get(`${API}/api/brand/demo-profile`, { withCredentials: true })
        .then(({ data }) => {
          // Persist only positive results. Negative responses don't poison the cache.
          if (data && data.is_demo) _cache = data;
          return data || { is_demo: false };
        })
        .catch(() => ({ is_demo: false }))
        .finally(() => { _inflight = null; });
    }
    _inflight.then((data) => {
      if (dead) return;
      setProfile(data);
      setLoading(false);
    });
    return () => { dead = true; };
  }, []);

  return { profile, loading, isDemo: !!(profile && profile.is_demo) };
}

/** Force-refresh — used after re-seeding demo data so pages pick up new profile. */
export function clearDemoProfileCache() {
  _cache = null;
  _inflight = null;
}
